import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BrandMark } from './BrandMark';

const Login: React.FC = () => {
  const [searchParams] = useSearchParams();
  const registered = searchParams.get('registered') === '1';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { login } = useAuth();

  const validateEmail = (value: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const validateForm = (): boolean => {
    let isValid = true;
    setEmailError('');
    setPasswordError('');

    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      isValid = false;
    }

    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      await login({ email, password });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Invalid email or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex app-mesh-bg">
      {/* Brand panel */}
      <aside className="hidden lg:flex lg:w-[46%] xl:w-[48%] relative overflow-hidden auth-panel-glow text-white flex-col justify-between p-12 xl:p-16">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '28px 28px',
        }} />
        <div className="relative z-10 animate-fade-in">
          <div className="flex items-center gap-3">
            <BrandMark size="md" inverted />
            <div>
              <p className="font-display text-xl font-bold tracking-tight">UMS</p>
              <p className="text-sm text-teal-100/80">Unibyte Management System</p>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md animate-slide-up">
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight">
            Run your business from one calm workspace.
          </h1>
          <p className="mt-5 text-base text-slate-200/85 leading-relaxed">
            Clients, tasks, billing, and communication — organized without the noise.
          </p>
        </div>
        <p className="relative z-10 text-xs text-teal-100/50">
          © {new Date().getFullYear()} Unibyte
        </p>
      </aside>

      {/* Form panel */}
      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-[420px] animate-slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <BrandMark size="md" />
            <div>
              <p className="font-display text-lg font-bold text-slate-900">UMS</p>
              <p className="text-xs text-slate-500">Unibyte Management System</p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900">
              Sign in
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              Welcome back. Enter your credentials to continue.
            </p>
          </div>

          <form className="space-y-5" onSubmit={handleSubmit}>
            {registered && (
              <div className="rounded-xl bg-emerald-50 border border-emerald-200/80 px-4 py-3">
                <p className="text-sm font-medium text-emerald-800">
                  Account created. You can now sign in.
                </p>
              </div>
            )}
            {error && (
              <div className="rounded-xl bg-rose-50 border border-rose-200/80 px-4 py-3 flex items-start gap-2">
                <svg className="h-5 w-5 text-rose-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-rose-800">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`w-full px-3.5 py-2.5 rounded-xl border bg-white text-slate-900 placeholder:text-slate-400 text-sm shadow-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-teal-600/30 focus:border-teal-600 ${
                  emailError ? 'border-rose-300' : 'border-slate-200'
                }`}
                placeholder="you@company.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailError) setEmailError('');
                }}
              />
              {emailError && <p className="mt-1.5 text-sm text-rose-600">{emailError}</p>}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => {
                    console.log('Forgot password clicked');
                  }}
                  className="text-sm font-medium text-teal-700 hover:text-teal-800 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                className={`w-full px-3.5 py-2.5 rounded-xl border bg-white text-slate-900 placeholder:text-slate-400 text-sm shadow-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-teal-600/30 focus:border-teal-600 ${
                  passwordError ? 'border-rose-300' : 'border-slate-200'
                }`}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
              />
              {passwordError && <p className="mt-1.5 text-sm text-rose-600">{passwordError}</p>}
            </div>

            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-600"
              />
              <span className="text-sm text-slate-600">Remember me</span>
            </label>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center items-center py-2.5 px-4 rounded-xl text-sm font-semibold text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-soft"
            >
              {isLoading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2.5 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in…
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default Login;
